import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Building,
  Calendar,
  ChevronRight,
  ClipboardList,
  Cog,
  Database,
  FileText,
  Folder,
  Home,
  LayoutGrid,
  LineChart,
  LogOut,
  Mail,
  MessageSquare,
  Monitor,
  Plus,
  Settings,
  Shield,
  User,
  UserCheck,
  Users,
} from 'lucide-react';

import { useAuthStore } from '../../store/AuthStore';
import { useRoleAccess } from '../common/roleAccess';
import { useFeatures } from '../../hooks/useFeatures';
import {
  getGrantNotificationsUnread,
  GRANT_NOTIFICATIONS_READ_EVENT,
} from '../../services/grantsApi';

// Define all possible navigation categories
const allNavigationCategories = {
  dashboard: {
    id: 'dashboard',
    items: [
      {
        path: '/dashboard',
        icon: <Home className="w-4 h-4" />,
        label: 'Dashboard',
      },
    ],
  },
  communications: {
    id: 'communications',
    icon: <MessageSquare className="w-3.5 h-3.5" />,
    label: 'Communications',
    items: [
      {
        path: '/comms',
        icon: <Mail className="w-4 h-4" />,
        label: 'Messages',
      },
      {
        path: '/documents',
        icon: <Folder className="w-4 h-4" />,
        label: 'Documents',
      },
      {
        path: '/redaction',
        icon: <Shield className="w-4 h-4" />,
        label: 'Redaction',
      },
      {
        path: '/calendar',
        icon: <Calendar className="w-4 h-4" />,
        label: 'Calendar',
      },
    ],
  },
  'data-insights': {
    id: 'dataInsights',
    icon: <Database className="w-3.5 h-3.5" />,
    label: 'Data & Insights',
    items: [
      {
        path: '/people',
        icon: <Users className="w-4 h-4" />,
        label: 'People',
      },
      {
        path: '/chat-analytics',
        icon: <LineChart className="w-4 h-4" />,
        label: 'Chat Analytics',
      },
    ],
  },
  administration: {
    id: 'administration',
    icon: <LayoutGrid className="w-3.5 h-3.5" />,
    label: 'Administration',
    items: [
      // { path: '/apps', icon: <Grid className="w-4 h-4" />, label: 'Apps' },
      {
        path: '/app',
        icon: <Users className="w-4 h-4" />,
        label: 'Apps',
        allowedRoles: ['owner', 'superAdmin', 'admin'],
      },
      {
        path: '/user-management',
        icon: <Users className="w-4 h-4" />,
        label: 'User Management',
        allowedRoles: ['owner', 'admin'],
      },
      {
        path: '/settings',
        icon: <Settings className="w-4 h-4" />,
        label: 'Settings',
        allowedRoles: ['owner', 'admin'],
      },
    ],
  },
  organizations: {
    id: 'organizations',
    icon: <Building className="w-3.5 h-3.5" />,
    label: 'Organizations',
    items: [
      {
        path: '/organizations',
        icon: <Building className="w-4 h-4" />,
        label: 'Manage Organizations',
      },
      {
        path: '/organizations/create',
        icon: <Plus className="w-4 h-4" />,
        label: 'Create Organization',
      },
    ],
  },
  users: {
    id: 'users',
    icon: <UserCheck className="w-3.5 h-3.5" />,
    label: 'User Management',
    items: [
      {
        path: '/users',
        icon: <Users className="w-4 h-4" />,
        label: 'All Users',
      },
      {
        path: '/users/sessions',
        icon: <Shield className="w-4 h-4" />,
        label: 'User Sessions',
      },
    ],
  },
  'system-settings': {
    id: 'systemSettings',
    icon: <Cog className="w-3.5 h-3.5" />,
    label: 'System Settings',
    items: [
      {
        path: '/system/settings',
        icon: <Settings className="w-4 h-4" />,
        label: 'System Config',
      },
      {
        path: '/system/logs',
        icon: <Database className="w-4 h-4" />,
        label: 'System Logs',
      },
    ],
  },
  're-entry': {
    id: 'reEntry',
    icon: <Shield className="w-3.5 h-3.5" />,
    label: 'Re-Entry',
    items: [
      {
        path: '/facilities',
        icon: <Building className="w-4 h-4" />,
        label: 'Facilities',
      },
      {
        path: '/re-entry',
        icon: <Shield className="w-4 h-4" />,
        label: 'Coordinators',
      },
      {
        path: '/inmate-onboarding-form-builder',
        icon: <UserCheck className="w-4 h-4" />,
        label: 'Inmate Onboarding Form Builder',
      },
      {
        path: '/reentry/resources',
        icon: <LayoutGrid className="w-4 h-4" />,
        label: 'Resources',
      },
      {
        path: '/organization-onboarding',
        icon: <Building className="w-4 h-4" />,
        label: 'Organization',
      },
    ],
  },
  reentry: {
    id: 'reentry',
    icon: <ClipboardList className="w-3.5 h-3.5" />,
    label: 'Re-Entry',
    items: [
      {
        path: '/reentry/forms',
        icon: <FileText className="w-4 h-4" />,
        label: 'Forms',
      },
      {
        path: '/reentry/devices',
        icon: <Monitor className="w-4 h-4" />,
        label: 'Devices',
      },
      {
        path: '/reentry/analytics',
        icon: <LineChart className="w-4 h-4" />,
        label: 'Analytics',
      },
    ],
  },
  grants: {
    id: 'grants',
    icon: <ClipboardList className="w-3.5 h-3.5" />,
    label: 'Grants',
    items: [
      {
        path: '/grants',
        icon: <ClipboardList className="w-4 h-4" />,
        label: 'Overview',
      },
      {
        path: '/grants/sponsorship',
        icon: <FileText className="w-4 h-4" />,
        label: 'Sponsorship',
      },
      {
        path: '/grants/applicants',
        icon: <Users className="w-4 h-4" />,
        label: 'Applications',
      },
    ],
  },
};

// Map paths to their required features
const FEATURE_REQUIREMENTS = {
  '/comms': 'messages',
  '/documents': 'documents',
  '/redaction': 'redaction',
  '/calendar': 'calendar',
  '/people': 'people',
  '/chat-analytics': 'chat_analytics',
  '/reentry/forms': 'reentry_forms',
  '/reentry/devices': 'reentry_forms',
  '/reentry/analytics': 'reentry_forms',
  '/grants': 'grants',
  '/grants/sponsorship': 'grants',
  '/grants/applicants': 'grants',
};

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { accessToken, user, logout } = useAuthStore();
  const { getSidebarItems } = useRoleAccess();
  const { hasFeature, isLoading: featuresLoading } = useFeatures();
  const currentPath = location.pathname;
  const [unreadGrantNotifications, setUnreadGrantNotifications] = useState(0);
  const unreadRequestGeneration = useRef(0);

  // Get allowed sidebar items for current user role
  const allowedSidebarItems = getSidebarItems();

  // State to track which categories are expanded
  const [expandedCategories, setExpandedCategories] = useState({
    communications: false,
    dataInsights: false,
    administration: false,
    organizations: false,
    users: false,
    systemSettings: false,
  });

  // Toggle category expansion
  const toggleCategory = (category, fallbackExpanded = false) => {
    const currentExpanded = expandedCategories[category] ?? fallbackExpanded;
    setExpandedCategories({
      ...expandedCategories,
      [category]: !currentExpanded,
    });
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/auth/login');
  };
  // Filter navigation categories based on user role AND enabled features
  const navigationCategories = allowedSidebarItems.reduce(
    (categories, item) => {
      const category = allNavigationCategories[item];
      if (!category) {
        return categories;
      }

      // Filter items by both role AND feature access
      const filteredItems = category.items.filter((categoryItem) => {
        // Check role access
        const hasRoleAccess =
          !categoryItem.allowedRoles ||
          categoryItem.allowedRoles.length === 0 ||
          categoryItem.allowedRoles.includes(user?.role);

        // Check feature access
        const requiredFeature = FEATURE_REQUIREMENTS[categoryItem.path];
        const hasFeatureAccess =
          !requiredFeature || hasFeature(requiredFeature);

        return hasRoleAccess && hasFeatureAccess;
      });

      if (filteredItems.length === 0) {
        return categories;
      }

      categories.push({
        ...category,
        items: filteredItems,
      });

      return categories;
    },
    [],
  );

  const grantsNavigationVisible = navigationCategories.some(
    (category) => category.id === 'grants',
  );

  useEffect(() => {
    if (!accessToken || !grantsNavigationVisible) {
      unreadRequestGeneration.current += 1;
      setUnreadGrantNotifications(0);
      return undefined;
    }

    setUnreadGrantNotifications(0);
    let activeController;
    const loadUnread = async () => {
      const requestGeneration = unreadRequestGeneration.current + 1;
      unreadRequestGeneration.current = requestGeneration;
      activeController?.abort();
      const controller = new AbortController();
      activeController = controller;

      try {
        const status = await getGrantNotificationsUnread({
          signal: controller.signal,
        });
        if (requestGeneration !== unreadRequestGeneration.current) return;
        setUnreadGrantNotifications(status.count);
      } catch (error) {
        if (
          requestGeneration !== unreadRequestGeneration.current ||
          error?.name === 'AbortError' ||
          error?.isAborted
        ) {
          return;
        }
        setUnreadGrantNotifications(0);
      } finally {
        if (activeController === controller) {
          activeController = undefined;
        }
      }
    };
    const handleNotificationsRead = (event) => {
      if (event.detail?.unread !== false || event.detail?.count !== 0) return;
      unreadRequestGeneration.current += 1;
      activeController?.abort();
      activeController = undefined;
      setUnreadGrantNotifications(event.detail.count);
    };
    const handleWindowFocus = () => {
      loadUnread();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadUnread();
      }
    };

    loadUnread();
    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener(
      GRANT_NOTIFICATIONS_READ_EVENT,
      handleNotificationsRead,
    );
    return () => {
      unreadRequestGeneration.current += 1;
      activeController?.abort();
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener(
        GRANT_NOTIFICATIONS_READ_EVENT,
        handleNotificationsRead,
      );
    };
  }, [accessToken, grantsNavigationVisible, user?.id]);

  const displayCategories =
    user?.role === 'superAdmin'
      ? (() => {
          const organizationsCategory = navigationCategories.find(
            (category) => category.id === 'organizations',
          );
          const administrationCategory = navigationCategories.find(
            (category) => category.id === 'administration',
          );

          if (!organizationsCategory && !administrationCategory) {
            return navigationCategories;
          }

          const onGovCategory = {
            id: 'ongov',
            icon: <Building className="w-3.5 h-3.5" />,
            label: 'OnGov',
            items: [],
            groups: [
              ...(organizationsCategory ? [organizationsCategory] : []),
              ...(administrationCategory ? [administrationCategory] : []),
            ],
          };

          const firstMergeIndex = navigationCategories.findIndex(
            (category) =>
              category.id === 'organizations' ||
              category.id === 'administration',
          );
          const categoriesWithoutMerged = navigationCategories.filter(
            (category) =>
              category.id !== 'organizations' &&
              category.id !== 'administration',
          );

          if (firstMergeIndex === -1) {
            return [...categoriesWithoutMerged, onGovCategory];
          }

          const insertIndex = Math.min(
            firstMergeIndex,
            categoriesWithoutMerged.length,
          );

          return [
            ...categoriesWithoutMerged.slice(0, insertIndex),
            onGovCategory,
            ...categoriesWithoutMerged.slice(insertIndex),
          ];
        })()
      : navigationCategories;

  // Count total visible feature items (excluding Dashboard and Administration)
  const totalFeatureItems = displayCategories
    .filter(
      (category) =>
        category.id !== 'dashboard' &&
        category.id !== 'administration' &&
        category.id !== 'organizations' &&
        category.id !== 'ongov' &&
        category.id !== 'users' &&
        category.id !== 'systemSettings',
    )
    .reduce((count, category) => count + category.items.length, 0);

  // Check if any item in a category is active (considering role-based filtering)
  const isCategoryActive = (categoryItems) => {
    return categoryItems
      .filter((item) => {
        // Filter items based on allowedRoles if specified
        if (item.allowedRoles && item.allowedRoles.length > 0) {
          return item.allowedRoles.includes(user?.role);
        }
        return true; // Show item if no role restriction
      })
      .some((item) =>
        item.matchPrefix
          ? currentPath.startsWith(item.matchPrefix)
          : item.path === currentPath,
      );
  };

  return (
    <div className="w-48 bg-white/70 backdrop-blur-lg rounded-2xl shadow-lg flex flex-col border border-white/80 m-4">
      {/* OnGov Logo */}
      <div className="p-3 border-b border-slate-200/50 flex items-center justify-center">
        <img
          width={32}
          height={32}
          src="/assets/logo.png"
          alt="OnGov Logo"
          className="rounded-full flex items-center justify-center"
        />
        <Link to="/" className="text-lg font-semibold text-indigo-700">
          OnGov
        </Link>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto px-2 py-2">
        <div className="space-y-1">
          {/* Render navigation categories based on user role */}
          {displayCategories.map((category, index) => {
            // If it's a single-item category (like dashboard), render directly
            if (
              category.items &&
              category.items.length === 1 &&
              category.id === 'dashboard'
            ) {
              return (
                <div key={category.id}>
                  {category.items.map((item) => {
                    const isActive = item.matchPrefix
                      ? currentPath.startsWith(item.matchPrefix)
                      : currentPath === item.path;

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={
                          'flex items-center px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-200 ' +
                          (isActive
                            ? 'bg-indigo-100 text-indigo-700 shadow-sm'
                            : 'text-slate-700 hover:bg-indigo-50/50')
                        }
                      >
                        <div className="mr-2.5">{item.icon}</div>
                        <span className="text-sm font-medium">
                          {item.label}
                        </span>
                      </Link>
                    );
                  })}
                  {/* Add separator after dashboard if there are more categories */}
                  {index === 0 && displayCategories.length > 1 && (
                    <div className="border-t border-slate-200/70 my-1.5"></div>
                  )}
                </div>
              );
            }

            // For collapsible categories
            const categoryItemsForActive = category.groups
              ? category.groups.flatMap((group) => group.items || [])
              : category.items;
            const isActive = isCategoryActive(categoryItemsForActive);
            const isExpanded = expandedCategories[category.id] ?? isActive;

            return (
              <div key={category.id}>
                {/* Category Header */}
                <button
                  type="button"
                  aria-expanded={isExpanded}
                  onClick={() => toggleCategory(category.id, isActive)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-all duration-200
                    ${
                      isActive
                        ? 'text-indigo-800 font-medium'
                        : 'text-slate-600 hover:text-slate-800'
                    }`}
                >
                  <div className="flex items-center">
                    <div
                      className={`mr-2 ${
                        isActive ? 'text-indigo-600' : 'text-slate-500'
                      }`}
                    >
                      {category.icon}
                    </div>
                    <span className="text-xs font-medium whitespace-nowrap">
                      {category.label}
                    </span>
                    {category.id === 'grants' &&
                    unreadGrantNotifications > 0 ? (
                      <span
                        aria-label="Unread grant notifications"
                        className="ml-1.5 h-2 w-2 rounded-full bg-red-600"
                        role="status"
                      />
                    ) : null}
                  </div>
                  <ChevronRight
                    className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${
                      isExpanded ? 'transform rotate-90' : ''
                    }`}
                  />
                </button>

                {/* Category Items */}
                <div
                  className={`transition-all duration-200 ease-in-out pl-3 overflow-x-hidden ${
                    isExpanded
                      ? 'max-h-80 overflow-y-auto'
                      : 'max-h-0 overflow-hidden'
                  }`}
                >
                  {category.id === 'ongov' && category.groups
                    ? category.groups.map((group) => {
                        const isGroupActive = isCategoryActive(group.items);
                        const isGroupExpanded =
                          expandedCategories[group.id] ?? isGroupActive;

                        return (
                          <div key={group.id}>
                            <button
                              type="button"
                              aria-expanded={isGroupExpanded}
                              onClick={() =>
                                toggleCategory(group.id, isGroupActive)
                              }
                              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-all duration-200 ${
                                isGroupActive
                                  ? 'text-indigo-800 font-medium'
                                  : 'text-slate-600 hover:text-slate-800'
                              }`}
                            >
                              <div className="flex items-center">
                                <div
                                  className={`mr-2 ${
                                    isGroupActive
                                      ? 'text-indigo-600'
                                      : 'text-slate-500'
                                  }`}
                                >
                                  {group.icon}
                                </div>
                                <span className="text-xs font-medium whitespace-nowrap">
                                  {group.label}
                                </span>
                              </div>
                              <ChevronRight
                                className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${
                                  isGroupExpanded ? 'transform rotate-90' : ''
                                }`}
                              />
                            </button>

                            <div
                              className={`transition-all duration-200 ease-in-out pl-3 overflow-x-hidden ${
                                isGroupExpanded
                                  ? 'max-h-80 overflow-y-auto'
                                  : 'max-h-0 overflow-hidden'
                              }`}
                            >
                              {group.items.map((item) => (
                                <Link
                                  key={item.path}
                                  to={item.path}
                                  className={
                                    'flex items-center px-3 py-2 rounded-lg cursor-pointer transition-all duration-200 ' +
                                    (currentPath === item.path
                                      ? 'bg-indigo-100 text-indigo-700 shadow-sm'
                                      : 'text-slate-600 hover:bg-indigo-50/50 hover:text-slate-800')
                                  }
                                >
                                  <div className="mr-2">{item.icon}</div>
                                  <span className="text-xs">{item.label}</span>
                                </Link>
                              ))}
                            </div>
                          </div>
                        );
                      })
                    : category.items.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={
                            'flex items-center px-3 py-2 rounded-lg cursor-pointer transition-all duration-200 ' +
                            (currentPath === item.path
                              ? 'bg-indigo-100 text-indigo-700 shadow-sm'
                              : 'text-slate-600 hover:bg-indigo-50/50 hover:text-slate-800')
                          }
                        >
                          <div className="mr-2">{item.icon}</div>
                          <span className="text-xs">{item.label}</span>
                        </Link>
                      ))}
                </div>
              </div>
            );
          })}

          {/* Show message if no feature items available (only after features have loaded) */}
          {totalFeatureItems === 0 && !featuresLoading && (
            <div className="px-3 py-4 text-center">
              <p className="text-xs text-slate-500">
                No features available. Contact your administrator for access.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Power Tools */}
      {/* <div className="px-2 pb-2">
        <div className="flex items-center px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-200 text-slate-700 hover:bg-indigo-50/50">
          <div className="w-4 h-4 bg-indigo-100 rounded-full flex items-center justify-center shadow-sm mr-2.5">
            <Plus className="w-2.5 h-2.5 text-indigo-700" />
          </div>
          <span className="text-sm font-medium">Power Tools</span>
        </div>
      </div> */}

      {/* User Profile and Logout */}
      <div className="p-3 border-t border-slate-200/50">
        <div className="flex items-center mb-2">
          <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center shadow-md">
            <User className="w-4 h-4 text-indigo-700" />
          </div>
          <div className="ml-2">
            <div className="text-xs font-medium text-slate-800">
              {user?.firstName} {user?.lastName}
            </div>
            <div className="text-xs text-slate-500 capitalize">
              {user?.role}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="w-full flex items-center px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-all duration-200"
        >
          <LogOut className="w-4 h-4 mr-2" />
          <span className="text-xs font-medium">Sign Out</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
